import React from 'react';

import cn from 'classnames';
import { isString } from 'lodash';
import { bool, node, oneOfType, shape, string } from 'prop-types';

import ButtonLink from 'components/Common/Buttons/ButtonLink';

import Button from 'components/Common/Buttons/ButtonV3';
import Flexbox from 'components/Common/Flexbox';
import Text from 'components/Common/Text';

import Speaker from 'modules/ElementaryCareers/components/Speaker';

import s from './EmojiEmptyState.css';

/**
 * Empty state that renders an emoji and text
 *
 * @param {string} buttonProps - can pass any props available to Button as an object
 * @param {string} className - used for additional container styles
 * @param {bool} compact - if true, will use compact text styles
 * @param {Object} emojiProps - name and label of emoji
 * @param {string | node} mainText - text to render under emoji - should typically be a string but can pass a node if text requires a nested link or something similar
 * @param {string | node} secondaryText - smaller subtext with the same implementation guidelines as described above in mainText
 */
const EmojiEmptyState = ({
  buttonProps,
  className,
  compact,
  emojiProps,
  mainText,
  secondaryText,
}) => {
  const ButtonComponent = buttonProps?.href ? ButtonLink : Button;

  return (
    <Flexbox
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      className={cn(s.root, className)}
    >
      <Text
        mb={2}
        variant={compact ? 'h2' : 'h1'}
        role="img"
        aria-label={emojiProps.label}
      >
        {emojiProps.emoji}
      </Text>
      {isString(mainText) ? (
        <Text
          variant={compact ? 'titleSmBold' : 'h5'}
          color="grayDark"
          component="h2"
          mb={compact ? 1.5 : 2}
        >
          <Speaker dataTtsId={mainText} smallIcon>
            {mainText}
          </Speaker>
        </Text>
      ) : (
        mainText
      )}
      {isString(secondaryText) ? (
        <Text
          variant={compact ? 'bodySm' : 'lead'}
          color="grayDark"
          align="center"
        >
          <Speaker dataTtsId={secondaryText} smallIcon>
            {secondaryText}
          </Speaker>
        </Text>
      ) : (
        secondaryText
      )}
      {buttonProps && (
        <ButtonComponent
          className={cn(
            s.actionButton,
            { [s.compact]: compact },
            buttonProps?.className,
          )}
          {...buttonProps}
        />
      )}
    </Flexbox>
  );
};

EmojiEmptyState.propTypes = {
  buttonProps: shape({}),
  className: string,
  compact: bool,
  emojiProps: shape({
    emoji: string,
    label: string,
  }).isRequired,
  mainText: oneOfType([string, node]).isRequired,
  secondaryText: oneOfType([string, node]),
};

EmojiEmptyState.defaultProps = {
  buttonProps: undefined,
  className: undefined,
  compact: false,
  secondaryText: undefined,
};

export default EmojiEmptyState;
